import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Smartphone,
  DollarSign,
  Eye,
  AlignJustify,
  PercentCircle,
  MousePointerClick,
  Percent,
  MoreHorizontal,
  BarChart3,
  Filter,
  X,
  Download,
  PiIcon,
} from "lucide-react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import AppAnalyticsFormModal from "../Components/Appanalytics/AppAnalyticsFormModal";
import { useAuth } from "../Auth/AuthContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import appAnalyticsService from "../Services/appAnalyticsService";
import PublisherApiService from "../Services/PublisherApiService";
import { format } from 'date-fns';
import adunitService from "../Services/adunitService";

export default function AppAnalytics() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const [data, setData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [publishers, setPublishers] = useState([]);
  const [adunits, setAdunits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    app: "",
    adunit: "",
    publisher: "",
    dateRange: [null, null],
  });

  // Memoized data fetching
  const fetchAnalytics = useCallback(async () => {
    setIsLoading(true);
    try {
      if (isAdmin) {
        const result = await appAnalyticsService.getAll();
        setData(result);
      } else {
        const result = await appAnalyticsService.getAllByPublisher(user.publisherId);
        setData(result);
      }
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadPublishers = useCallback(async () => {
    try {
      const result = await PublisherApiService.getAllPublishers();
      setPublishers(result);
    } catch (error) {
      toast.error("Failed to load publishers");
    }
  }, []);

  const loadAdunits = useCallback(async () => {
    try {
      const result = await adunitService.getAdUnits();
      setAdunits(result);
    } catch (error) {
      toast.error("Failed to load publishers");
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();

    if (isAdmin) {
      loadPublishers();
      loadAdunits();
    }
  }, [fetchAnalytics, loadPublishers, loadAdunits, isAdmin]);

  // Extract unique values for filters
  const filterOptions = useMemo(() => {
    const apps = [...new Set(data.map((item) => item.propertyName))];
    const adunits = [...new Set(data.map((item) => item.adunit))];
    const uniquePublishers = [...new Set(data.map((item) => item.publisher))];

    return { apps, adunits, uniquePublishers };
  }, [data]);

  // Apply filters
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const date = new Date(item.date);
      return (
        (!filters.app || item.propertyName === filters.app) &&
        (!filters.adunit || item.adunit === filters.adunit) &&
        (!filters.publisher || item.publisher === filters.publisher) &&
        (!filters.dateRange[0] || date >= filters.dateRange[0]) &&
        (!filters.dateRange[1] || date <= filters.dateRange[1])
      );
    });
  }, [data, filters]);

  // Calculate metrics
const metrics = useMemo(() => {
  const totalRevenue = filteredData.reduce((sum, d) => sum + d.revenue, 0);
  const totalImpressions = filteredData.reduce((sum, d) => sum + d.impressions, 0);
  const totalAdRequests = filteredData.reduce((sum, d) => sum + d.adRequests, 0);
  const totalClicks = filteredData.reduce((sum, d) => sum + d.clicks, 0);

  const getTotal = (key) =>
    filteredData.reduce((sum, d) => sum + d[key], 0);

    const count = filteredData.length || 1; // avoid divide by 0

  const getAverage = (key) =>
    filteredData.reduce((sum, d) => sum + d[key], 0) / count;

  return {
    totalRevenue,
    totalImpressions,
    totalAdRequests,
    totalClicks,
    totalViewability: getAverage('viewability'),
    totalCTR: getAverage('ctr'),
    totalFillRate: getAverage('fillRate'),
    totalECPM: getAverage('ecpm')
  };
}, [filteredData]);


  // Modal handlers
  const openModal = useCallback((item = null) => {
    setEditItem(item);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setEditItem(null);
    setModalOpen(false);
  }, []);

  const handleDelete = useCallback(async (id) => {
    if (window.confirm("Are you sure you want to delete this app analytics record?")) {
      try {
        await appAnalyticsService.delete(id);
        toast.success("App analytics deleted");
        fetchAnalytics();
      } catch (err) {
        toast.error("Failed to delete");
      }
    }
  }, [fetchAnalytics]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newItem = {
      id: editItem?.id || 0,
      date: formData.get("date"),
      revenue: Number(formData.get("revenue")),
      adRequests: Number(formData.get("adRequests")),
      impressions: Number(formData.get("impressions")),
      clicks: Number(formData.get("clicks")),
      viewability: Number(formData.get("viewability")),
      ecpm: Number(formData.get("ecpm")),
      fillRate: Number(formData.get("fillRate")),
      ctr: Number(formData.get("ctr")),
      adunit: formData.get("adunit"),
      propertyId: parseInt(formData.get("propertyId")),
      propertyName: formData.get("app"),
      publisher: formData.get("publisher"),
    };

    try {
      if (editItem) {
        await appAnalyticsService.update(editItem.id, newItem);
        toast.success("Web analytics updated");
      } else {
        await appAnalyticsService.create(newItem);
        toast.success("Web analytics added");
      }
      closeModal();
      fetchAnalytics();
    } catch (error) {
      if (error.response?.data?.errors) {
        toast.error(Object.values(error.response.data.errors).flat().join("\n"));
      } else if (error.response?.data?.title) {
        toast.error(error.response.data.title);
      } else {
        toast.error("Operation failed");
      }
    }
  }, [editItem, closeModal, fetchAnalytics]);

  // Filter handlers
  const handleFilterChange = useCallback((name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleDateRangeChange = useCallback((dates) => {
    setFilters(prev => ({ ...prev, dateRange: dates }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      app: "",
      adunit: "",
      publisher: "",
      dateRange: [null, null],
    });
  }, []);

  // Table columns
  const columns = useMemo(() => [
    {
      name: "Date",
      selector: row => format(new Date(row.date), 'dd-MMM-yyyy'),
      sortable: true,
      width: "120px",
    },
    {
      name: "Publisher",
      selector: row => row.publisher,
      sortable: true,
      minWidth: "120px",
      maxWidth: "180px",
    },
    {
      name: "App",
      selector: row => row.propertyName,
      sortable: true,
      width: "320px",
    },
    {
      name: "App Name",
      selector: row => row.appName,
      sortable: true,
      width: "180px",
    },
    {
      name: "Adunit",
      selector: row => row.adunit,
      sortable: true,
      width: "150px",
    },
    {
      name: "Revenue ($)",
      selector: row => row.revenue.toFixed(2),
      width: "100px",
    },
    {
      name: "eCPM ($)",
      selector: row => row.ecpm.toFixed(2),
      width: "80px",
    },
    {
      name: "Ad Impressions",
      selector: row => row.impressions.toLocaleString(),
      width: "100px",
    },
    {
      name: "Ad Requests",
      selector: row => row.adRequests.toLocaleString(),
      width: "100px",
    },
    {
      name: "Fill Rate (%)",
      selector: row => row.fillRate.toFixed(1),
      width: "100px",
    },
    {
      name: "Viewability (%)",
      selector: row => `${row.viewability.toFixed(1)}%`,
      width: "100px",
    },
    {
      name: "Clicks",
      selector: row => row.clicks.toLocaleString(),
      width: "70px",
    },
    {
      name: "CTR (%)",
      selector: row => row.ctr.toFixed(2),
      width: "70px",
    },
    ...(isAdmin ? [{
      name: <MoreHorizontal className="w-4 h-4 text-gray-500" />,
      cell: row => (
        <div className="space-x-2">
          <button
            onClick={() => openModal(row)}
            className="text-blue-600 hover:underline text-sm"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="text-red-500 hover:underline text-sm"
          >
            Delete
          </button>
        </div>
      ),
      button: true,
      width: "100px",
    }] : []),
  ], [isAdmin, openModal, handleDelete]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="p-4 sm:p-6 space-y-6 overflow-x-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <motion.h2
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2"
        >
          <Smartphone className="w-5 h-5 text-indigo-500" />
          App Analytics
        </motion.h2>

        <div className="flex gap-3">

          {isAdmin && (
            <button
              onClick={() => openModal()}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg shadow hover:bg-indigo-700 transition-colors"
            >
              + Add App Analytics
            </button>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-lg shadow p-4 border border-gray-100"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-md font-semibold flex items-center">
            <Filter className="w-4 h-4 mr-2 text-indigo-500" />
            Filters
          </h3>
          <button
            onClick={resetFilters}
            className="text-sm text-gray-500 hover:text-indigo-600 flex items-center"
          >
            <X className="w-3 h-3 mr-1" />
            Reset all
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* App Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">App</label>
            <select
              value={filters.app}
              onChange={(e) => handleFilterChange("app", e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">All Apps</option>
              {filterOptions.apps.map(app => (
                <option key={app} value={app}>{app}</option>
              ))}
            </select>
          </div>

          {/* Ad Unit Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ad Unit</label>
            <select
              value={filters.adunit}
              onChange={(e) => handleFilterChange("adunit", e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">All Ad Units</option>
              {filterOptions.adunits.map(unit => (
                <option key={unit} value={unit}>{unit}</option>
              ))}
            </select>
          </div>

          {/* Publisher Filter */}
          {isAdmin ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Publisher</label>
              <select
                value={filters.publisher}
                onChange={(e) => handleFilterChange("publisher", e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">All Publishers</option>
                {filterOptions.uniquePublishers.map(pub => (
                  <option key={pub} value={pub}>{pub}</option>
                ))}
              </select>
            </div>
          ) : null}

          {/* Date Range Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <DatePicker
              selectsRange
              startDate={filters.dateRange[0]}
              endDate={filters.dateRange[1]}
              onChange={handleDateRangeChange}
              placeholderText="Select date range"
              className="w-full rounded-md border border-gray-300 bg-white py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              isClearable
            />
          </div>
        </div>
      </motion.div>

      {/* Metrics Display */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Revenue"
          value={`$${metrics.totalRevenue.toFixed(2)}`}
          icon={DollarSign}
          color="text-green-500"
        />
        <MetricCard
          title="Total Impressions"
          value={metrics.totalImpressions.toLocaleString()}
          icon={Eye}
          color="text-blue-500"
        />
        <MetricCard
          title="Total Ad Requests"
          value={metrics.totalAdRequests.toLocaleString()}
          icon={AlignJustify}
          color="text-yellow-500"
        />
        <MetricCard
          title="Total Clicks"
          value={metrics.totalClicks.toLocaleString()}
          icon={MousePointerClick}
          color="text-pink-500"
        />
        <MetricCard
          title="Total Viewability"
          value={`${metrics.totalViewability.toFixed(2)}%`}
          icon={PercentCircle}
          color="text-purple-500"
        />
        <MetricCard
          title="Total CTR"
          value={`${metrics.totalCTR.toFixed(2)}%`}
          icon={Percent}
          color="text-indigo-400"
        />
        <MetricCard
          title="Total Fill Rate"
          value={`${metrics.totalFillRate.toFixed(2)}%`}
          icon={PiIcon}
          color="text-cyan-500"
        />
        <MetricCard
          title="Total eCPM"
          value={`$${metrics.totalECPM.toFixed(2)}`}
          icon={BarChart3}
          color="text-indigo-500"
        />
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="min-w-[600px]">
          <ChartCard
            title="Revenue by App"
            data={filteredData}
            dataKey="revenue"
            color="#6366f1"
            xAxisKey="appName"
          />
        </div>

        <div className="min-w-[600px]">
          <ChartCard
            title="Impressions Overview"
            data={filteredData}
            dataKey="impressions"
            color="#10b981"
            xAxisKey="appName"
          />
        </div>
      </div>

      {/* Data Table */}
      {/* Data Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-lg shadow overflow-hidden max-w-[calc(100vw-2rem)]"
      >
        <div className="overflow-x-auto">
          <DataTable
            columns={columns}
            data={filteredData}
            progressPending={isLoading}
            pagination
            highlightOnHover
            customStyles={{
              headCells: {
                style: {
                  fontWeight: "600",
                  backgroundColor: "#f9fafb",
                },
              },
              table: {
                style: {
                  minWidth: "max-content",
                },
              },
            }}
          />
        </div>
      </motion.div>

      {/* Edit/Add Modal */}
      <AnimatePresence>
        {modalOpen && (
          <AppAnalyticsFormModal
            modalOpen={modalOpen}
            handleSubmit={handleSubmit}
            editItem={editItem}
            closeModal={closeModal}
            publishers={publishers}
            adunits={adunits}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Helper components
function MetricCard({ title, value, icon: Icon, color }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white p-4 rounded-lg shadow border border-gray-100"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-xl font-bold mt-1">{value}</p>
        </div>
        <div className={`p-2 rounded-md ${color.replace('text', 'bg') + '100'} ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </motion.div>
  );
}

function ChartCard({ title, data, dataKey, color, xAxisKey = "appName" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-lg shadow p-4 border border-gray-100"
    >
      <h3 className="text-lg font-semibold text-gray-800 mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey={xAxisKey}
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => value.length > 10 ? `${value.substring(0, 10)}...` : value}
          />
          <YAxis />
          <Tooltip
            contentStyle={{
              backgroundColor: '#fff',
              borderRadius: '6px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb'
            }}
          />
          <Bar
            dataKey={dataKey}
            fill={color}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </motion.div>
  );
}